'use client'

import CreationToolCutOutMode from 'creation/src/creation-tools/types/cut-out-mode'
import { cn } from '@/util/cn'
import { cutOutModeFill, cutOutModeIcon } from '../../presentation/cut-out-mode'

type Props = {
    mode: CreationToolCutOutMode
    isActive: boolean
}

/**
 * Port of cut-out/button-icon.tsx:59-74. Legacy overlays two absolutely
 * positioned layers over one another — the static SVG icon and an animated
 * sprite sheet — and swaps between them on hover. That swap needs a `:hover`
 * on the *parent* to drive both children in opposite directions at once,
 * which has no direct Tailwind utility, so it is built with `group` /
 * `group-hover` instead (button-icon.tsx:37-46).
 *
 * Legacy's `svg path[fill]` selector (button-icon.tsx:33-35) targets
 * descendant `<path>` elements directly, because a CSS presentation
 * attribute on `<path fill="#hex">` only loses to a rule that selects the
 * path itself — a rule that merely sets `fill` on the ancestor `<svg>`
 * doesn't cascade past the path's own attribute. `cutOutModeFill(mode)`
 * already carries that `[&_path[fill]]:` variant baked into its literal
 * value (presentation/cut-out-mode.ts), so calling it here genuinely drives
 * the path's colour — it isn't decorative. The active branch spells out
 * `[&_path[fill]]:fill-black` directly since black isn't one of the four
 * mode colours the helper maps.
 */
const ToolIcon = ({ mode, isActive }: Props) => {
    const Icon = cutOutModeIcon(mode)
    const animationUrl = CreationToolCutOutMode.iconAnimationUrl(mode)

    return (
        <div className="group relative h-[120px] w-full">
            <Icon
                className={cn(
                    'absolute top-0 left-1/2 -ml-[60px] h-[120px] w-[120px] transition-opacity duration-200 ease-out group-hover:opacity-0',
                    isActive ? '[&_path[fill]]:fill-black' : cutOutModeFill(mode)
                )}
            />
            <div
                data-testid="tool-sprite"
                style={{ backgroundImage: `url(${animationUrl})` }}
                className="pointer-events-none absolute top-0 left-1/2 -ml-[60px] h-[120px] w-[120px] bg-[length:auto_100%] bg-no-repeat opacity-0 transition-opacity duration-200 ease-out group-hover:animate-ct-icon-sprite group-hover:opacity-100"
            />
        </div>
    )
}

export default ToolIcon
